// Public store pages: browse and search assets, an asset's page (live 3D preview with its params, stats, licence and
// price, versions, install instructions) and a creator's assets. Data comes from the store service through
// store-client.ts (the sample store while the service isn't running).

import { getCatalogue } from "../catalogue.ts";
import { creatorName } from "../components.ts";
import { clear, h, link, toast } from "../dom.ts";
import { api } from "../api.ts";
import { itemPath, priceLabel, viewerCurrency, type Prices } from "../money.ts";
import { setTitle } from "../router.ts";
import { itemOffer, NOT_AVAILABLE, selling, visibleItems } from "../selling.ts";
import { currentMe } from "../session.ts";
import { wornOnBody } from "../asset-preview.ts";
import { startAssetPreview } from "../preview-frame.ts";
import { ownedAssets, store, type StoreBackend } from "../store-client.ts";
import { cliLines, creditsText, diffSection, forkedFromLine, forksSection, historySection, loadLineage, offersSection, openLicenceLine, useActions, type LineageCtx } from "./asset-lineage.ts";
import {
  ASSET_KIND_LABEL,
  type Manifest,
  assetFilterFromQuery,
  assetFilterToQuery,
  assetPath,
  changedParams,
  controlsFor,
  COLLECTIONS,
  CONVENTION_LABEL,
  creatorStorePath,
  inCollection,
  fmtBytes,
  fmtCount,
  installSnippets,
  isAssetId,
  LICENCE_LABEL,
  moneyItemId,
  snap,
  type AssetFilter,
  type AssetSummary,
  type Control,
  type Stats,
  type Value,
} from "../store.ts";

/**
 * An item's price as currency (`$4.99`), or "Free". `usdMinor` is the store's numeric price in US cents; `prices`
 * (money's `{USD, EUR}`) gives the viewer's own currency when we have it (wave 10).
 */
export const priceText = (usdMinor: number, prices?: Prices | null): string => priceLabel(usdMinor, viewerCurrency(), prices);
/** `priceText`, or "Not available yet" for a paid item while nothing is sold (wave 9). */
export const offerText = (usdMinor: number, isOn: boolean = selling(), prices?: Prices | null): string => (itemOffer(usdMinor, isOn) === "unavailable" ? NOT_AVAILABLE : priceText(usdMinor, prices));

/**
 * A paid item's prices from money (`GET /v1/items/:id` → `prices`), for a viewer whose wallet isn't in USD. Store
 * listings carry only the USD price; on any failure we show that.
 */
export async function itemPrices(itemId: string, usdMinor: number, isOn: boolean, currency = viewerCurrency(), fetchItem: (id: string) => Promise<unknown> = (id) => api<unknown>("money", itemPath(id))): Promise<Prices | null> {
  if (!isOn || !(usdMinor > 0) || currency === "USD") return null;
  try {
    const r = await fetchItem(itemId);
    const p = r && typeof r === "object" ? (r as { prices?: unknown }).prices : null;
    return p && typeof p === "object" ? (p as Prices) : null;
  } catch {
    return null;
  }
}

/** The browse filter as searched: without selling only free items, and no price sort (wave 9). */
export function sellingFilter(f: AssetFilter, isOn: boolean): AssetFilter {
  return isOn ? f : { ...f, price: "free", sort: f.sort === "price" ? "new" : f.sort };
}
const kindOne = (k: string): string => (ASSET_KIND_LABEL[k] ?? k).replace(/s$/, "");
/** Conventions as players read them (store.ts's labels are for creators: "Humanoid rig"). */
const CONVENTION_PLAYER: Record<string, string> = { "thrdm.humanoid/1": "Fits every avatar" };
const conventionText = (c: string): string => CONVENTION_PLAYER[c] ?? CONVENTION_LABEL[c] ?? c;
const byName = (c: { name?: string | null; handle: string }): string => creatorName(c.name, c.handle);
/** An asset's title for players: a format note some titles carry ("Small Grey Crate (static GLB)") stays off. */
export const assetTitle = (t: string): string => t.replace(/\s*\((static\s+)?(glb|gltf)\)\s*$|\s*\(humanoid rig\)\s*$/i, "") || t;
/** Tags that describe the file, not the thing: left off the chips players see. */
const FILE_TAGS = new Set(["static", "glb", "gltf", "baked", "procedural", "low-poly", "humanoid", "cosmetic"]);

export function sampleNote(b: StoreBackend): HTMLElement | null {
  return b.live ? null : h("p", { class: "note sample small" }, "Sample data (offline page work): the starter packages with made-up version histories. Changes last until you reload. Turn it off with ?sample-store=0.");
}

export function assetCard(a: AssetSummary, owned: Set<string> | null): HTMLElement {
  const mine = owned?.has(moneyItemId(a.id)) ?? false;
  return h(
    "li",
    { class: "card asset-card" },
    link(
      assetPath(a.id),
      { class: "card-link", "aria-label": `${assetTitle(a.title)} by ${byName(a.creator)}, ${offerText(a.price)}${mine ? ", owned" : ""}` },
      h("div", { class: "card-art asset-art" }, a.previewUrl ? h("img", { src: a.previewUrl, alt: "", loading: "lazy" }) : h("span", { class: "card-art-title" }, assetTitle(a.title))),
      h(
        "div",
        { class: "card-meta" },
        h("span", { class: "card-title" }, assetTitle(a.title)),
        h("span", { class: "card-sub" }, `${kindOne(a.kind)} · ${byName(a.creator)}`),
        h("span", { class: "asset-price" }, mine ? h("span", { class: "badge ok" }, "Owned") : offerText(a.price)),
      ),
    ),
  );
}

export function assetGrid(list: AssetSummary[], owned: Set<string> | null, empty = "Nothing here yet."): HTMLElement {
  if (!list.length) return h("p", { class: "dim" }, empty);
  return h("ul", { class: "grid asset-grid" }, list.map((a) => assetCard(a, owned)));
}

// ---- browse ------------------------------------------------------------------------------------------------------

/**
 * Sets inside a collection (the Workshop's banners): `?set=<id>` narrows the browse to these pieces. A set lists ids
 * because the pack's tags don't name its sets (a dead tree isn't tagged "graveyard").
 */
export const SETS: Record<string, { title: string; ids: string[] }> = {
  graveyard: { title: "The graveyard", ids: ["threedom/halloween-gravestone", "threedom/halloween-fence", "threedom/halloween-dead-tree"] },
  "haunted-house": { title: "The haunted house", ids: ["threedom/halloween-house-kit", "threedom/halloween-lantern", "threedom/halloween-candles"] },
};
export const inSet = (a: Pick<AssetSummary, "id">, set: string | null): boolean => !set || (SETS[set]?.ids.includes(a.id) ?? false);

/**
 * Queries a `/store` request answers with the filtered browse rather than the Workshop zone (once the zone holds
 * /store). `all=1` is the browse's own marker: with every filter at its default the query would otherwise be empty,
 * and a reload of bare /store lands on the Workshop (review 2026-10-19).
 */
const BROWSE_KEYS = ["collection", "set", "q", "kind", "convention", "price", "sort", "all"];
export function storeQueryWantsBrowse(q: URLSearchParams): boolean {
  return BROWSE_KEYS.some((k) => q.has(k));
}

/** The browse's URL query for a filter query string: never empty, so the URL still means the browse. */
export const browseSearch = (qs: string): string => qs || "?all=1";

export async function storeBrowsePage(_p: unknown, query: URLSearchParams): Promise<HTMLElement> {
  const backend = await store();
  const on = selling();
  // without selling (wave 9) paid items leave the lists, and ?price= / ?sort=price are ignored
  const all = visibleItems(await backend.search({}), on);
  const owned = await ownedAssets(currentMe()?.id ?? null);
  let f: AssetFilter = assetFilterFromQuery(query);
  if (!on) f = { ...f, price: null, sort: f.sort === "price" ? "new" : f.sort };
  let set: string | null = SETS[query.get("set") ?? ""] ? query.get("set") : null;
  setTitle(set ? SETS[set]!.title : f.collection ? COLLECTIONS[f.collection]!.title : f.kind ? ASSET_KIND_LABEL[f.kind] ?? "Assets" : "Assets");
  // wave 11: the Halloween collection (the first-party pack, all free) leads the page while it has anything in it
  const haunt = all.filter((a) => inCollection(a, "halloween"));
  const collectionBox = h("div");

  const results = h("div", { class: "results" });
  const count = h("p", { class: "dim count", "aria-live": "polite" });
  const kindRow = h("div", { class: "filter-row tabs", role: "group", "aria-label": "Kind" });
  const conventionRow = h("div", { class: "filter-row", role: "group", "aria-label": "Rig" });
  const priceRow = h("div", { class: "filter-row", role: "group", "aria-label": on ? "Price" : "Sort" });
  const pill = (text: string, on: boolean, onclick: () => void) => h("button", { type: "button", class: `pill${on ? " on" : ""}`, "aria-pressed": on ? "true" : "false", onclick }, text);

  let seq = 0;
  let timer = 0;
  const update = (next: Partial<AssetFilter>, push = false, delay = 0) => {
    f = { ...f, ...next };
    if ("collection" in next && !next.collection) set = null; // "Show all assets" clears the set too
    let qs = assetFilterToQuery(f);
    if (set) qs += `${qs ? "&" : "?"}set=${encodeURIComponent(set)}`;
    qs = browseSearch(qs);
    if (location.search !== qs) history[push ? "pushState" : "replaceState"](null, "", `/store${qs}`);
    drawFilters();
    drawCollection();
    window.clearTimeout(timer);
    timer = window.setTimeout(() => void drawResults(), delay);
  };

  const kinds = [...new Set(all.map((a) => a.kind))].sort((a, b) => Object.keys(ASSET_KIND_LABEL).indexOf(a) - Object.keys(ASSET_KIND_LABEL).indexOf(b));
  const conventions = [...new Set(all.flatMap((a) => a.conventions))].sort();
  const drawFilters = () => {
    clear(kindRow, pill("All", !f.kind, () => update({ kind: null }, true)), kinds.map((k) => pill(ASSET_KIND_LABEL[k] ?? k, f.kind === k, () => update({ kind: f.kind === k ? null : k }, true))));
    clear(
      conventionRow,
      // one convention is no choice: the row shows only once there are two to pick between
      conventions.length > 1 ? pill("Any", !f.convention, () => update({ convention: null }, true)) : null,
      conventions.length > 1 ? conventions.map((c) => pill(conventionText(c), f.convention === c, () => update({ convention: f.convention === c ? null : c }, true))) : null,
    );
    clear(
      priceRow,
      on ? pill("Any price", !f.price, () => update({ price: null }, true)) : null,
      on ? pill("Free", f.price === "free", () => update({ price: f.price === "free" ? null : "free" }, true)) : null,
      on ? pill("Paid", f.price === "paid", () => update({ price: f.price === "paid" ? null : "paid" }, true)) : null,
      h(
        "label",
        { class: "sort" },
        h("span", { class: "sr-only" }, "Sort"),
        h(
          "select",
          { onchange: (e: Event) => update({ sort: (e.target as HTMLSelectElement).value as AssetFilter["sort"] }) },
          [
            ["new", "Newest"],
            ["title", "A to Z"],
            ...(on ? [["price", "Price"]] : []),
          ].map(([v, t]) => h("option", { value: v, selected: (f.sort ?? "new") === v }, t)),
        ),
      ),
    );
  };
  const drawCollection = () => {
    const c = COLLECTIONS.halloween!;
    clear(
      collectionBox,
      haunt.length && !f.collection
        ? h(
            "section",
            { class: "collection haunt-collection", "aria-label": c.title },
            h("div", { class: "row-head" }, h("h2", null, c.title), h("button", { type: "button", class: "pill", onclick: () => update({ collection: "halloween" }, true) }, `All ${haunt.length}`)),
            h("p", { class: "dim small" }, c.lead, " Free to use in any haunt. ", link("/haunt", null, "Open Haunt")),
            h("ul", { class: "grid asset-grid collection-track" }, haunt.slice(0, 8).map((a) => assetCard(a, owned))),
          )
        : f.collection
          ? h("p", { class: "collection-on" }, h("strong", null, set ? SETS[set]!.title : COLLECTIONS[f.collection]!.title), " ", h("button", { type: "button", class: "pill on", "aria-pressed": "true", onclick: () => update({ collection: null }, true) }, "Show all assets"))
          : null,
    );
  };
  const drawResults = async () => {
    const mine = ++seq;
    const list = await backend.search(sellingFilter({ ...f, collection: null }, on)).then((l) => visibleItems(l, on).filter((a) => (!f.collection || inCollection(a, f.collection)) && inSet(a, set))).catch((e) => {
      clear(results, h("p", { class: "error" }, e instanceof Error ? e.message : String(e)));
      return null;
    });
    if (!list || mine !== seq) return;
    count.textContent = `${list.length} ${list.length === 1 ? "asset" : "assets"}`;
    clear(results, assetGrid(list, owned, "No matches. Try fewer filters or another word."));
  };
  drawFilters();
  drawCollection();
  void drawResults();

  const search = h("input", {
    type: "search",
    id: "aq",
    placeholder: "Search characters, clothing, props, creators",
    value: f.q ?? "",
    autocomplete: "off",
    oninput: (e: Event) => update({ q: (e.target as HTMLInputElement).value }, false, 200),
  });

  return h(
    "div",
    { class: "browse store" },
    h("h1", null, "Assets"),
    h("p", { class: "lead" }, "Characters, clothing and props for three.js games, made by creators on Banger. They share one rig and one scale, so a shirt from one creator fits a character from another."),
    sampleNote(backend),
    collectionBox,
    h("form", { class: "search", role: "search", onsubmit: (e: Event) => e.preventDefault() }, h("label", { for: "aq", class: "sr-only" }, "Search assets"), search),
    kindRow,
    conventionRow,
    priceRow,
    count,
    results,
    h("p", { class: "dim small store-foot" }, "Made something? ", link("/dashboard/assets", null, "Publish your assets"), " from the creator dashboard."),
  );
}

// ---- asset page --------------------------------------------------------------------------------------------------

/** Settings by name as players read them; keys not listed get store.ts's label from the key. */
const PARAM_LABEL: Record<string, string> = {
  face: "Face",
  faceSize: "Face size",
  stemLength: "Stem",
  ribDepth: "Grooves",
  squash: "Squatness",
  lit: "Candle inside",
  glow: "Glow",
  light: "Light the room",
  lightIntensity: "Light strength",
  colour: "Colour",
  stem: "Stem colour",
};
/** Settings a game maker wants and a player doesn't: always under "More settings". */
const TECHNICAL = new Set(["skinned", "wall", "roomy", "light", "lightIntensity", "seed", "lod", "detail"]);
/**
 * Settings an item doesn't offer on its page: the free Pumpkin Head's candle is the "Visit three haunts" reward
 * (threedom/halloween-pumpkin-head-lit), so its viewer never lights it (orchestrator, 2026-10-18).
 */
export const LOCKED_PARAMS: Record<string, string[]> = { "threedom/halloween-pumpkin-head": ["lit", "glow"] };
/** How many settings show before "More settings". */
const MAIN_SETTINGS = 4;

/** A slider's value in words: a count, a size in cm or m, or how far along its range ("70%"), never raw units. */
export function humanValue(c: Extract<Control, { kind: "range" }>, v: number): string {
  if (c.step >= 1 && Number.isInteger(c.min)) return String(v);
  if (/\bmetres?\b/i.test(c.hint ?? "")) return v < 1 ? `${Math.round(v * 100)} cm` : `${Number(v.toFixed(2))} m`;
  return `${Math.round(((v - c.min) / (c.max - c.min || 1)) * 100)}%`;
}

const nice = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

function control(c: Control, onChange: (v: Value) => void): HTMLElement {
  const id = `p-${c.key}`;
  const text = PARAM_LABEL[c.key] ?? c.label;
  // the creator's description is a tooltip, not a line under every slider
  const labelEl = h("label", { for: id, title: c.hint ?? null }, text);
  if (c.kind === "range") {
    const out = h("span", { class: "value" }, humanValue(c, c.value));
    return h(
      "div",
      { class: "slider" },
      h("div", { class: "slider-head" }, labelEl, out),
      h("input", {
        type: "range",
        id,
        min: c.min,
        max: c.max,
        step: c.step,
        value: c.value,
        oninput: (e: Event) => {
          const v = snap(Number((e.target as HTMLInputElement).value), c.step);
          out.textContent = humanValue(c, v);
          onChange(v);
        },
      }),
    );
  }
  if (c.kind === "toggle") {
    return h("label", { class: "check param-check", title: c.hint ?? null }, h("input", { type: "checkbox", id, checked: c.value, onchange: (e: Event) => onChange((e.target as HTMLInputElement).checked) }), text);
  }
  if (c.kind === "colour") {
    return h("div", { class: "param-row" }, labelEl, h("input", { type: "color", class: "swatch", id, value: c.value, oninput: (e: Event) => onChange((e.target as HTMLInputElement).value) }));
  }
  if (c.kind === "choice") {
    return h(
      "div",
      { class: "param-row" },
      labelEl,
      h("select", { id, onchange: (e: Event) => onChange((e.target as HTMLSelectElement).value) }, c.options.map((o) => h("option", { value: o, selected: o === c.value }, nice(o)))),
    );
  }
  return h("div", { class: "param-row" }, labelEl, h("input", { id, value: c.value, onchange: (e: Event) => onChange((e.target as HTMLInputElement).value) }));
}

/**
 * One-tap looks above the sliders ("Classic / Scary / Goofy") for anything with a carved face (the Halloween
 * pumpkins). Each sets only the params the asset has.
 */
export function presetsFor(m: Pick<Manifest, "params">): [string, Record<string, Value>][] {
  const props = m.params?.properties ?? {};
  const faces = props.face?.enum?.map(String) ?? [];
  if (!faces.includes("classic") || !faces.includes("scary")) return [];
  const only = (p: Record<string, Value>) => Object.fromEntries(Object.entries(p).filter(([k]) => k in props));
  const out: [string, Record<string, Value>][] = [
    ["Classic", only({ face: "classic", teeth: 3, faceSize: props.faceSize?.default as number })],
    ["Scary", only({ face: "scary", teeth: Math.min(6, (props.teeth?.maximum as number | undefined) ?? 6), faceSize: props.faceSize?.maximum as number })],
  ];
  if (faces.includes("happy")) out.push(["Goofy", only({ face: "happy", teeth: 1, faceSize: props.faceSize?.minimum as number })]);
  return out.map(([n, p]) => [n, Object.fromEntries(Object.entries(p).filter(([, v]) => v !== undefined))]);
}

function codeBlock(title: string, code: string, hint?: HTMLElement | string): HTMLElement {
  const pre = h("pre", { class: "code" }, h("code", null, code));
  return h(
    "div",
    { class: "install-block" },
    h(
      "div",
      { class: "install-head" },
      h("h3", null, title),
      h(
        "button",
        {
          type: "button",
          class: "btn small ghost",
          onclick: async () => {
            try {
              await navigator.clipboard.writeText(pre.textContent ?? "");
              toast("Copied");
            } catch {
              toast("Couldn't copy. Select the text instead.");
            }
          },
        },
        "Copy",
      ),
    ),
    hint ? h("p", { class: "dim small" }, hint) : null,
    pre,
  );
}

export function statsList(s: Stats, budget?: { triangles?: number; drawCalls?: number }): HTMLElement {
  const item = (k: string, v: string | null, b?: number) => h("div", null, h("dt", null, k), h("dd", null, v ?? "—"), b !== undefined ? h("span", { class: "dim small" }, `budget ${fmtCount(b)}`) : null);
  return h(
    "dl",
    { class: "stats asset-stats" },
    item("Triangles", s.triangles !== undefined ? fmtCount(s.triangles) : null, budget?.triangles),
    item("Draw calls", s.drawCalls !== undefined ? String(s.drawCalls) : null, budget?.drawCalls),
    item("Download", s.bytes !== undefined ? fmtBytes(s.bytes) : null),
  );
}

/** ">=0.180.0 <0.190.0" → "r180 to r189" */
export function threeRange(peer?: string): string | null {
  if (!peer) return null;
  const lo = /^>=\s*0\.(\d+)/.exec(peer)?.[1];
  const hi = /<\s*0\.(\d+)\.0/.exec(peer)?.[1];
  return lo && hi ? `r${lo} to r${Number(hi) - 1}` : peer;
}

/**
 * The preset a `look` names (the launcher's preset tiles open `?viewer=1&look=Scary`): matched like the Workshop's
 * generator, by the preset's name, ignoring case and a trailing " look".
 */
export function lookPreset(m: Pick<Manifest, "params">, look: string | null): Record<string, Value> | null {
  const want = (look ?? "").trim().toLowerCase().replace(/\s+look$/, "");
  if (!want) return null;
  return presetsFor(m).find(([n]) => n.toLowerCase() === want)?.[1] ?? null;
}

export async function assetPage(params: Record<string, string>, query: URLSearchParams = new URLSearchParams()): Promise<HTMLElement> {
  const id = `${params.creator}/${params.name}`;
  if (!isAssetId(id)) throw new Error("That isn't an asset address.");
  // the launcher's live viewer window: the stage and its settings only, no site chrome (wave 13)
  const viewer = query.get("viewer") === "1";
  const backend = await store();
  const a = await backend.asset(id);
  if (!a) throw new Error("No asset by that name in the store.");
  setTitle(assetTitle(a.title));
  const me = currentMe();
  // the store's licence lookup (money's record) for this asset; money's inventory when the store can't say
  const lic = me && a.price > 0 ? await backend.licensed(a.id) : null;
  const owned = lic === null ? await ownedAssets(me?.id ?? null) : null;
  const isOwned = lic ?? owned?.has(moneyItemId(a.id)) ?? false;
  const on = selling();
  // a paid item while nothing is sold (wave 9): its page stays, but says "Not available yet" (owners still install)
  const unavailable = itemOffer(a.price, on) === "unavailable" && !isOwned;
  const prices = await itemPrices(moneyItemId(a.id), a.price, on);
  const live = backend.openFiles(a);
  const runtime = await backend.runtime();
  const m = a.manifest;
  const worn = wornOnBody(m);

  let values: Record<string, Value> = {};
  for (const c of controlsFor(m, {}, worn)) values[c.key] = c.value;
  const look = lookPreset(m, query.get("look"));
  if (look) values = { ...values, ...look };
  const measured: Stats = { ...a.stats };

  // ---- preview ----
  const stageNote = h("p", { class: "stage-note" }, "Loading the 3D preview…");
  // the store's picture shows at once, with a shimmer, and fades when the 3D view is up (it took 4-8 s of blank)
  const poster = a.previewUrl ? h("img", { class: "stage-poster", src: a.previewUrl, alt: "" }) : null;
  const stage = h("div", { class: "stage asset-stage loading", role: "img", "aria-label": `3D preview of ${assetTitle(a.title)}` }, poster, h("span", { class: "stage-shimmer", "aria-hidden": "true" }), stageNote);
  const ready = () => stage.classList.remove("loading", "failed");
  // no drawn 3D frame in 6 s (no GPU, a stalled renderer): stop waiting and keep the store's picture, never a blank box
  setTimeout(() => {
    if (!stage.classList.contains("loading") || stage.classList.contains("still")) return;
    stage.classList.replace("loading", "failed");
    stageNote.textContent = a.previewUrl ? "The 3D view didn't start; this is the store's picture." : "The 3D view didn't start.";
  }, 6_000);
  const statsBox = h("div", null, statsList(measured, m.budget));
  const statsNote = h("p", { class: "dim small" }, a.stats?.triangles !== undefined ? "From the store's checks at default params." : worn ? "Counted from the item's model at default params." : "Counted in your browser at the params you've set.");
  // paid assets: the store's public low-detail model, never their licence-gated files or code
  const model = !live && a.previewModel ? a.previewModel : null;
  const preview = live || model
    ? startAssetPreview(stage, {
    base: model ?? a.base,
    manifest: m,
    mode: model ? "preview" : "live",
    onNote: (t) => {
      stageNote.textContent = t;
      if (/drag to turn/i.test(t)) ready();
      else if (/unavailable/i.test(t)) {
        stage.classList.remove("loading"); // keep the picture, stop the shimmer (also when 3D was already up and the GPU went)
        stage.classList.add("failed");
        console.warn("asset preview:", t); // the technical reason for developers; players get plain words
        stageNote.textContent = a.previewUrl ? "3D isn't available in this browser; this is the store's picture." : "3D isn't available in this browser.";
      }
    },
    onStats: (s) => {
      if (a.stats?.triangles !== undefined) return; // the store's numbers win
      Object.assign(measured, s);
      clear(statsBox, statsList(measured, m.budget));
    },
  })
    : staticPreview(stage, stageNote, a.previewUrl);

  // ---- params + install snippets (the snippets follow the sliders) ----
  const snippets = h("div", { class: "install" });
  const drawInstall = () => {
    if (unavailable) return clear(snippets, h("p", { class: "dim" }, "This asset isn't available yet. Free assets install from here as usual."));
    const s = installSnippets(a.id, changedParams(m, values), { physics: !!m.physics, paid: a.price > 0 && !isOwned, runtime, site: location.origin });
    clear(
      snippets,
      codeBlock(
        "With your agent (MCP)",
        s.mcp,
        a.price > 0
          ? isOwned
            ? "You own a licence, so it installs free on any machine."
            : `Your agent shows you ${priceText(a.price, prices)} and buys only after you say yes. One purchase covers every later version.`
          : "The store's install tool writes it to your project's assets/ folder, pins the version in assets.lock, and checks the store's signature and every file hash first.",
      ),
      h("details", { class: "mcp-config" }, h("summary", null, "Add the store to your agent"), h("pre", { class: "code" }, h("code", null, s.mcpConfig)), h("p", { class: "dim small" }, "Its tools: ", h("code", null, "search"), ", ", h("code", null, "get"), ", ", h("code", null, "preview"), ", ", h("code", null, "install"), ". Creators also get ", h("code", null, "publish"), " and ", h("code", null, "yank"), ".")),
      codeBlock("In a three.js game", s.three, "Assets get your game's own three.js. Each frame is yours: call the asset's behaviours from your loop."),
      codeBlock(
        "From a terminal",
        s.cli,
        a.price > 0 && !isOwned
          ? h("span", null, `It shows you ${priceText(a.price, prices)} and stops. Add `, h("code", { class: "nowrap" }, "--confirm"), " only once you've decided to buy.")
          : "The same checks as your agent: the store's signature and every file hash, then assets/ and assets.lock. The CLI's thrdm outdated lists newer versions.",
      ),
    );
  };
  const controls = controlsFor(m, values, worn);
  const paramsPanel = h("div", { class: "params" });
  const presets = presetsFor(m);
  const changed = () => {
    if (!model) preview.show(values); // the preview model is fixed at the defaults
    drawInstall();
  };
  const drawParams = () => {
    const locked = LOCKED_PARAMS[a.id] ?? [];
    const all = controlsFor(m, values, worn).filter((c) => !locked.includes(c.key)).map((c) => ({
      c,
      el: control(c, (v) => {
        values = { ...values, [c.key]: v };
        markPreset();
        changed();
      }),
    }));
    // the settings a player reaches for first: what the looks change, then choices, colours and the candle, then sliders
    const looks = new Set(presets.flatMap(([, p]) => Object.keys(p)));
    const rank = (c: Control) => (looks.has(c.key) ? 0 : c.kind !== "range" || c.key === "glow" ? 1 : 2);
    const main = all
      .filter(({ c }) => !TECHNICAL.has(c.key))
      .map((x, i) => ({ x, i }))
      .sort((p, q) => rank(p.x.c) - rank(q.x.c) || p.i - q.i)
      .slice(0, MAIN_SETTINGS)
      .sort((p, q) => p.i - q.i)
      .map(({ x }) => x);
    const more = all.filter((x) => !main.includes(x));
    clear(
      paramsPanel,
      presets.length ? presetRow : null,
      controls.length === 0 ? h("p", { class: "dim small" }, "This asset has no settings.") : main.map((x) => x.el),
      more.length ? h("details", { class: "more-settings" }, h("summary", null, `More settings (${more.length})`), more.map((x) => x.el)) : null,
      controls.length
        ? h(
            "button",
            {
              type: "button",
              class: "btn small ghost",
              onclick: () => {
                values = {};
                for (const c of controlsFor(m, {}, worn)) values[c.key] = c.value;
                drawParams();
                changed();
              },
            },
            "Reset",
          )
        : null,
      live ? null : h("p", { class: "dim small" }, model ? "The 3D view is a lower-detail preview at the default settings: the full model and these settings come with a licence. The install code below follows them." : "The picture shows the default settings. The install code below still follows them."),
      worn && (live || model) ? h("p", { class: "dim small" }, "Shown on the standard body. It fits every avatar: the wearer's height and shape come from their own avatar.") : null,
    );
    markPreset();
  };
  const presetRow = h(
    "div",
    { class: "filter-row presets", role: "group", "aria-label": "Looks" },
    presets.map(([name, p]) =>
      h(
        "button",
        {
          type: "button",
          class: "pill",
          "data-preset": name,
          "aria-pressed": "false",
          onclick: () => {
            values = { ...values, ...p };
            drawParams();
            changed();
          },
        },
        name,
      ),
    ),
  );
  /** light up the preset the settings match, if any */
  function markPreset(): void {
    for (const b of presetRow.querySelectorAll<HTMLElement>("[data-preset]")) {
      const p = presets.find(([n]) => n === b.dataset.preset)?.[1] ?? {};
      const on = Object.entries(p).every(([k, v]) => values[k] === v);
      b.classList.toggle("on", on);
      b.setAttribute("aria-pressed", on ? "true" : "false");
    }
  }
  drawParams();
  drawInstall();
  preview.show(values);
  if (!(live || model)) ready();

  if (viewer) {
    return h(
      "article",
      { class: "asset-viewer" },
      h("div", { class: "stage-wrap" }, stage),
      h("section", { class: "panel params-panel viewer-panel", "aria-label": "Settings" }, h("h1", null, assetTitle(a.title)), h("p", { class: "dim small" }, "by ", byName(a.creator)), paramsPanel),
    );
  }

  const ctx: LineageCtx = {
    backend,
    a,
    me,
    data: await loadLineage(backend, a),
    params: () => changedParams(m, values),
    openFiles: live,
    importCode: () => installSnippets(a.id, changedParams(m, values), { physics: !!m.physics, paid: a.price > 0 && !isOwned, runtime, site: location.origin }).three,
  };
  const credits = creditsText(ctx);

  const licence = LICENCE_LABEL[a.licence] ?? { name: a.licence, text: "" };
  const three = threeRange(m.engines?.three?.peer?.three);

  return h(
    "article",
    { class: "asset-page" },
    sampleNote(backend),
    h(
      "div",
      { class: "asset-top" },
      h("div", { class: "stage-wrap" }, stage),
      h(
        "div",
        { class: "asset-side" },
        h("p", { class: "dim small crumbs" }, link("/store", null, "Assets"), " / ", link(`/store?kind=${encodeURIComponent(a.kind)}`, null, ASSET_KIND_LABEL[a.kind] ?? a.kind)),
        h("h1", null, assetTitle(a.title)),
        h("p", { class: "dim" }, "by ", link(creatorStorePath(a.creator.handle), null, byName(a.creator)), a.creator.verified ? h("span", { class: "badge ok verified" }, "Verified") : null, ` · v${a.version}`),
        forkedFromLine(ctx),
        h(
          "div",
          { class: "buy-box" },
          on || !(a.price > 0) ? h("div", { class: "asset-price big" }, priceText(a.price, prices)) : isOwned ? null : h("div", { class: "asset-price big" }, NOT_AVAILABLE),
          isOwned ? h("span", { class: "badge ok" }, "Owned") : null,
          h("p", { class: "small" }, h("strong", null, `${licence.name} licence. `), licence.text),
          openLicenceLine(a),
          on && a.price > 0 && !isOwned ? h("p", { class: "dim small" }, "You pay when you install it from a terminal or your agent, after you confirm. One purchase covers every later version.") : null,
          on && a.cosmetic ? h("p", { class: "dim small" }, "Players can also buy it to wear on their avatar. Wearing it and shipping it in a game are separate purchases.") : null,
        ),
        unavailable ? null : useActions(ctx),
        h("ul", { class: "chips" }, h("li", null, kindOne(a.kind)), a.conventions.map((c) => h("li", null, conventionText(c))), m.physics ? h("li", null, "Physics (Rapier)") : null, a.tags.filter((t) => !FILE_TAGS.has(t.toLowerCase())).slice(0, 5).map((t) => h("li", null, t))),
        h("section", { class: "panel params-panel" }, h("h2", null, "Try it"), paramsPanel),
      ),
    ),
    h(
      "div",
      { class: "asset-body" },
      h("section", { class: "section" }, h("h2", null, "Stats"), statsBox, statsNote),
      h(
        "section",
        { class: "section" },
        h("h2", null, "Details"),
        h(
          "dl",
          { class: "facts" },
          fact("Works with", three ? `three.js ${three}` : "Any 3D game or tool (a ready-made model)"),
          fact("Scale", `${m.units ?? "metres"}, ${m.up ?? "+Y"} up`),
          m.conventions?.length ? fact("Rig", m.conventions.map(conventionText).join(", ")) : null,
          m.wear?.slot ? fact("Worn on", m.wear.slot) : null,
          fact("Code may use", m.capabilities?.length ? m.capabilities.join(", ") : "Nothing beyond its own files"),
          m.behaviours?.length ? fact("Behaviours", m.behaviours.join(", ")) : null,
          fact("Deterministic", m.deterministic ? "Yes: the same params and seed always give the same result" : "No"),
          a.downloads !== undefined ? fact("Installs", fmtCount(a.downloads)) : null,
          credits ? h("div", null, h("dt", null, "Includes changes from"), h("dd", null, credits)) : null,
        ),
      ),
      historySection(ctx),
      forksSection(ctx),
      diffSection(ctx),
      offersSection(ctx),
      // install code is for game makers: folded away so a player sees the asset, not a terminal
      h("details", { class: "section install-details" }, h("summary", null, h("h2", null, "For developers: terminal, agent and three.js")), snippets, unavailable ? null : cliLines(ctx)),
    ),
  );
}

/** The store's picture in place of the 3D view (paid assets: their files need a licence). */
function staticPreview(stage: HTMLElement, note: HTMLElement, url: string | null): { show(p: Record<string, Value>): void } {
  stage.classList.add("still");
  if (url) stage.prepend(h("img", { src: url, alt: "", class: "stage-img" }));
  note.textContent = url ? "Picture at the default settings." : "No preview picture.";
  return { show() {} };
}

function fact(k: string, v: string): HTMLElement {
  return h("div", null, h("dt", null, k), h("dd", null, v));
}

// ---- creator's assets --------------------------------------------------------------------------------------------

export async function storeCreatorPage(params: Record<string, string>): Promise<HTMLElement> {
  const backend = await store();
  const c = await backend.creator(params.handle ?? "");
  if (!c) throw new Error("No creator by that name on Banger.");
  const name = creatorName(c.name, c.handle);
  setTitle(`${name}: assets`);
  const owned = await ownedAssets(currentMe()?.id ?? null);
  const on = selling();
  const shown = visibleItems(c.assets, on);
  const paid = on ? c.assets.filter((a) => a.price > 0).length : 0;
  const games = c.creatorId ? await getCatalogue().then((cat) => cat.creators.some((x) => x.id === c.creatorId), () => false) : false;
  return h(
    "article",
    { class: "creator" },
    sampleNote(backend),
    h(
      "header",
      { class: "creator-head" },
      h("div", { class: "avatar-badge", "aria-hidden": "true" }, name.slice(0, 1).toUpperCase()),
      h(
        "div",
        null,
        h("h1", null, name),
        h("p", { class: "dim" }, `${shown.length} ${shown.length === 1 ? "asset" : "assets"}`, paid ? ` · ${paid} paid` : null, c.creatorId ? [" · ", link(`/c/${c.creatorId}`, null, games ? "Games" : "Creator page")] : null),
      ),
    ),
    h("section", { class: "section" }, h("h2", null, "Assets"), assetGrid(shown, owned, "No assets yet.")),
  );
}
